import React from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import HomeIcon from "@mui/icons-material/Home";
import InfoIcon from "@mui/icons-material/Info";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ContactSupportIcon from "@mui/icons-material/ContactSupport";
import SEOHead from "../components/SEOHead";

const Sitemap = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const sitemapStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "サイトマップ",
    description:
      "わかば接骨院のサイトマップ。ウェブサイトの構造をご案内します。",
    url: "https://wakabaseto.jp/sitemap",
  };

  const pages = [
    {
      title: "ホーム",
              description: "わかば接骨院のホームページ。当院の特徴と施術方針をご紹介。",
      path: "/",
      icon: <HomeIcon />,
    },
    {
      title: "当院について",
      description:
        "わかば接骨院の理念と院長紹介。36年の経験を持つ柔道整復師・鍼灸師が在籍。",
      path: "/about",
      icon: <InfoIcon />,
    },
    {
              title: "施術メニュー",
        description: "接骨施術と鍼灸施術の詳細メニュー。五十肩・腰痛施術に特化。",
      path: "/services",
      icon: <LocalHospitalIcon />,
    },
    {
      title: "アクセス",
      description:
        "わかば接骨院へのアクセス情報。新瀬戸駅徒歩10分、専用駐車場9台。",
      path: "/access",
      icon: <LocationOnIcon />,
    },
    {
      title: "お問い合わせ",
      description:
        "わかば接骨院へのお問い合わせ・ご予約。お気軽にご連絡ください。",
      path: "/contact",
      icon: <ContactSupportIcon />,
    },
  ];

  const legalPages = [
    {
      title: "プライバシーポリシー",
      description: "個人情報の取り扱いについて説明しています。",
      path: "/privacy-policy",
    },
    {
      title: "利用規約",
      description: "ウェブサイトの利用条件について説明しています。",
      path: "/terms-of-service",
    },
  ];

  return (
    <>
      <SEOHead
        title="サイトマップ"
        description="わかば接骨院のサイトマップ。ウェブサイトの構造をご案内します。"
        keywords="サイトマップ,わかば接骨院,ページ一覧"
        canonical="/sitemap"
        structuredData={sitemapStructuredData}
      />
      <Box>
        {/* Hero Section */}
        <Box
          sx={{
            bgcolor: "primary.light",
            py: { xs: 4, sm: 6, md: 8 },
            mb: { xs: 3, sm: 4, md: 6 },
          }}
        >
          <Container maxWidth="lg">
            <Typography
              variant={isMobile ? "h2" : "h1"}
              gutterBottom
              align="center"
              sx={{
                fontSize: {
                  xs: "2rem",
                  sm: isTablet ? "2.25rem" : "2.5rem",
                  md: "3rem",
                },
              }}
            >
              サイトマップ
            </Typography>
            <Typography
              variant={isMobile ? "h6" : "h5"}
              align="center"
              sx={{
                mt: { xs: 1, md: 2 },
                color: "text.primary",
                fontSize: isTablet ? "1.1rem" : undefined,
              }}
            >
              ウェブサイトの構造
            </Typography>
          </Container>
        </Box>

        <Container maxWidth="lg">
          {/* Main Pages */}
          <Paper elevation={3} sx={{ p: { xs: 3, md: 6 }, mb: 4 }}>
            <Typography
              variant={isMobile ? "h4" : "h3"}
              gutterBottom
              sx={{ mb: 4 }}
            >
              メインページ
            </Typography>
            <List>
              {pages.map((page, index) => (
                <ListItem
                  key={index}
                  component={RouterLink}
                  to={page.path}
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    mb: 2,
                    textDecoration: "none",
                    color: "inherit",
                    "&:hover": {
                      bgcolor: "action.hover",
                      borderColor: "primary.main",
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: "primary.main" }}>
                    {page.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={page.title}
                    secondary={page.description}
                    primaryTypographyProps={{
                      variant: "h6",
                      fontWeight: "medium",
                    }}
                    secondaryTypographyProps={{
                      variant: "body2",
                      color: "text.secondary",
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>

          {/* Legal Pages */}
          <Paper elevation={3} sx={{ p: { xs: 3, md: 6 }, mb: 4 }}>
            <Typography
              variant={isMobile ? "h4" : "h3"}
              gutterBottom
              sx={{ mb: 4 }}
            >
              法的ページ
            </Typography>
            <List>
              {legalPages.map((page, index) => (
                <ListItem
                  key={index}
                  component={RouterLink}
                  to={page.path}
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    mb: 2,
                    textDecoration: "none",
                    color: "inherit",
                    "&:hover": {
                      bgcolor: "action.hover",
                      borderColor: "primary.main",
                    },
                  }}
                >
                  <ListItemText
                    primary={page.title}
                    secondary={page.description}
                    primaryTypographyProps={{
                      variant: "h6",
                      fontWeight: "medium",
                    }}
                    secondaryTypographyProps={{
                      variant: "body2",
                      color: "text.secondary",
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>

          {/* Contact Information */}
          <Paper elevation={3} sx={{ p: { xs: 3, md: 6 }, mb: { xs: 4, md: 6 } }}>
            <Typography
              variant={isMobile ? "h4" : "h3"}
              gutterBottom
              sx={{ mb: 4 }}
            >
              お問い合わせ
            </Typography>
            <Typography variant="body1" paragraph>
              わかば接骨院へのお問い合わせは、以下の方法でお願いいたします。
            </Typography>
            <Box sx={{ pl: 2 }}>
              <Typography variant="h6" gutterBottom>
                電話でのお問い合わせ
              </Typography>
              <Typography variant="body1" paragraph>
                TEL: 0561-83-3833
              </Typography>

              <Typography variant="h6" gutterBottom>
                住所
              </Typography>
              <Typography variant="body1" paragraph>
                〒489-0809
                <br />
                愛知県瀬戸市共栄通3-5
              </Typography>

              <Typography variant="h6" gutterBottom>
                営業時間
              </Typography>
              <Typography variant="body1" paragraph>
                平日（水曜以外）: 午前9時から12時、午後4時から8時
                <br />
                水曜・土曜: 午前9時から12時
                <br />
                日曜・祝日: 休診
              </Typography>
            </Box>
          </Paper>
        </Container>
      </Box>
    </>
  );
};

export default Sitemap;
